import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ScrollText, ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const sections = [
    {
      title: '1. Acceptance of Terms',
      content: `By accessing and using Velis Browser, you agree to be bound by these Terms of Use and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this software.`,
    },
    {
      title: '2. Use License',
      content: `Permission is granted to temporarily download one copy of Velis Browser for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:

• Modify or copy the materials
• Use the materials for any commercial purpose or public display
• Attempt to decompile or reverse engineer any software contained in Velis Browser
• Remove any copyright or other proprietary notations from the materials
• Transfer the materials to another person or "mirror" the materials on any other server`,
    },
    {
      title: '3. Disclaimer',
      content: `The materials within Velis Browser are provided on an 'as is' basis. Mild-Ads makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.`,
    },
    {
      title: '4. Limitations',
      content: `In no event shall Mild-Ads or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use Velis Browser, even if Mild-Ads or a Mild-Ads authorized representative has been notified orally or in writing of the possibility of such damage.`,
    },
    {
      title: '5. Third-Party Services',
      content: `Velis Browser may integrate or rely on third-party services to provide certain functionality. Search functionality within Velis Browser is Powered by Yahoo Search. Velis Browser is not responsible for the content, accuracy, or availability of third-party services and websites.`,
    },
    {
      title: '6. Accuracy of Materials',
      content: `The materials appearing in Velis Browser could include technical, typographical, or photographic errors. Mild-Ads does not warrant that any of the materials are accurate, complete, or current. Mild-Ads may make changes to the materials at any time without notice.`,
    },
    {
      title: '7. Links',
      content: `Mild-Ads has not reviewed all of the sites linked to Velis Browser and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by Mild-Ads. Use of any such linked website is at the user's own risk.`,
    },
    {
      title: '8. Modifications',
      content: `Mild-Ads may revise these Terms of Use at any time without notice. By using Velis Browser, you are agreeing to be bound by the then current version of these Terms of Use.`,
    },
    {
      title: '9. Governing Law',
      content: `These terms and conditions are governed by and construed in accordance with applicable laws, and you irrevocably submit to the exclusive jurisdiction of the courts in that location.`,
    },
    {
      title: '10. Contact Us',
      content: `If you have any questions about these Terms of Use, please contact us at contact@velis-browser.com

Mild-Ads LTD
Knaan 5 Street, Or Yehuda, 6041923, Israel`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0f1923] text-[#e2e8f0] font-sans">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#3b82f6]/10 rounded-full blur-[160px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-[#1d4ed8]/10 rounded-full blur-[160px]" />
      </div>

      <nav className="relative z-50 px-6 py-5 border-b border-white/5 backdrop-blur-md bg-[#0f1923]/70">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight">Velis Browser</span>
          </Link>
          <Link to="/" className="text-[#94a3b8] hover:text-white transition-colors font-medium flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <header className="relative z-10 py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="p-4 bg-gradient-to-br from-[#3b82f6]/20 to-[#1d4ed8]/20 rounded-2xl border border-white/10">
              <ScrollText className="w-8 h-8 text-[#60a5fa]" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">Terms of Use</h1>
              <p className="text-[#94a3b8] mt-2">The terms governing your use of Velis Browser</p>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="relative z-10 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ delay: index * 0.04 }}
                className="bg-[#1a2735]/60 rounded-2xl border border-white/5 p-8"
              >
                <h2 className="text-2xl font-bold mb-4 text-white">{section.title}</h2>
                <div className="text-[#cbd5e1] leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </main>

      <footer className="relative z-10 py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold">Velis Browser</span>
          </div>
          <div className="flex gap-8 text-sm text-[#94a3b8]">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-white">Terms of Use</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
          <p className="text-[#94a3b8]/70 text-sm">© {new Date().getFullYear()} Mild-Ads LTD</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
